import { useState } from "react";
import { endPoll, getPollEndpoint } from "apis/poll";
import { post, get } from "utils/request";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { ROUTES, TOAST_CONFIG } from "../../../constants";
import Loading from "../../atoms/Loading";
import PollModal from "./PollModal";
import useRouter from "../../hooks/useRouter";

type Props = {
  onModalClose: () => void;
  pollId: string;
  handleRefetchActivations: () => void;
};

function PollResultsModalContainer({ onModalClose, pollId, handleRefetchActivations }: Props) {
  const router = useRouter();
  const { m: magnetId } = router.query;
  const [loading, setLoading] = useState(false);

  const handleGetPollDetailsFailure = (errorMessage: string, error: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      router.go(`/${ROUTES.NOT_FOUND}`);
      return;
    }
    throw new Error(errorMessage);
  };

  const getPollDetails = () =>
    get(getPollEndpoint(magnetId, pollId), {
      processError: handleGetPollDetailsFailure,
    });

  const {
    isLoading: isPollDetailsLoading,
    error: pollDetailsError,
    data: pollDetails,
    refetch,
    isRefetching,
  } = useQuery<PollDataForBrand>({
    queryKey: [magnetId, pollId],
    queryFn: getPollDetails,
  });

  const handleEndPollSuccess = () => {
    handleRefetchActivations();
    setLoading(false);
    onModalClose();
    toast.success("Poll Ended Successfully", TOAST_CONFIG);
  };

  const handleEndPollError = (errorMessage: string) => {
    setLoading(false);
    toast.error(errorMessage, TOAST_CONFIG);
    throw new Error(errorMessage);
  };

  const handleEndPollButtonClick = async () => {
    setLoading(true);
    post(
      endPoll(magnetId, pollId),
      {},
      {
        processData: handleEndPollSuccess,
        processError: handleEndPollError,
      },
    );
  };

  const handleCancelButtonClick = () => {
    onModalClose();
  };

  if (isPollDetailsLoading) return <Loading />;
  if (pollDetailsError) return null;
  if (!pollDetails) return null;

  const maxVotePercentages = pollDetails.choices.map((choices) =>
    Math.max(...choices.map((choice) => choice.percentage)),
  );

  return (
    <PollModal
      pollData={pollDetails}
      isModalOpen
      onModalClose={onModalClose}
      handleEndPollButtonClick={handleEndPollButtonClick}
      handleCancelButtonClick={handleCancelButtonClick}
      maxVotePercentages={maxVotePercentages}
      loading={loading}
      isBrandPollResultsModal
      refetch={refetch}
      isRefetching={isRefetching}
    />
  );
}
export default PollResultsModalContainer;
